import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	DialogActions,
	Button,
	Box,
	Alert,
	Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { ESortDirection } from 'ui/EasTable/types';
import { DatePickerComponent } from 'ui/DatePickerComponent';
import { UserAvatarMenu } from 'ui/UserAvatarMenu';
import { TUser } from 'redux/reducers/user/types';
import { PAGE_SIZE } from 'services/constants/env';
import {
	createEmCommentRequest,
	updateEmCommentRequest,
} from 'redux/reducers/emManagement/reducer';
import { TEmCommentUser } from 'redux/reducers/emManagement/types';
import {
	getCreateEmCommentLoading,
	getCreateEmCommentSuccess,
	getUpdateEmCommentLoading,
	getUpdateEmCommentSuccess,
} from 'redux/reducers/emManagement/selectors';
import { TAddAndEditCommentDialogProps } from './types';
import { EDatePickerAllowedRange } from '../../../../../../ui/DatePickerComponent/constants';

export const AddAndEditCommentDialog = ({
	open,
	setClose,
	type = 'add',
	idToEdit = null,
	initialComment = '',
	initialScheduledActionOn = null,
	initialOperatorsToNotify = [],
}: TAddAndEditCommentDialogProps) => {
	const dispatch = useDispatch();
	const { emId } = useParams();
	const isCreateLoading = useSelector(getCreateEmCommentLoading);
	const isUpdateLoading = useSelector(getUpdateEmCommentLoading);
	const isCreateSuccess = useSelector(getCreateEmCommentSuccess);
	const isUpdateSuccess = useSelector(getUpdateEmCommentSuccess);

	const [comment, setComment] = useState<string>(initialComment ?? '');
	const [scheduledActionOn, setScheduledActionOn] = useState<string | null>(
		initialScheduledActionOn ?? null,
	);
	const [operatorsToNotify, setOperatorsToNotify] = useState<TEmCommentUser[]>(
		Array.isArray(initialOperatorsToNotify) ? initialOperatorsToNotify : [],
	);
	const [submitted, setSubmitted] = useState<boolean>(false);

	const eraseForm = () => {
		setComment(initialComment ?? '');
		setScheduledActionOn(initialScheduledActionOn ?? null);
		setOperatorsToNotify(Array.isArray(initialOperatorsToNotify) ? initialOperatorsToNotify : []);
	};

	const handleClose = () => {
		eraseForm();
		setClose();
	};

	// useEffect(() => {
	// 	if (!open) return;
	// 	setComment(initialComment ?? '');
	// 	setScheduledActionOn(initialScheduledActionOn ?? null);
	// 	setOperatorsToNotify(Array.isArray(initialOperatorsToNotify) ? initialOperatorsToNotify : []);
	// }, [open, initialComment, initialScheduledActionOn, initialOperatorsToNotify]);

	// useEffect(() => {
	// 	if (!open || !submitted) return;
	// 	if (type === 'add' && isCreateSuccess) setClose();
	// 	if (type === 'edit' && isUpdateSuccess) setClose();
	// }, [open, submitted, type, isCreateSuccess, isUpdateSuccess, setClose]);

	// useEffect(() => {
	// 	// reset submit marker every time dialog is (re)opened
	// 	if (open) setSubmitted(false);
	// }, [open]);

	const isSaving = isCreateLoading || isUpdateLoading;

	const operatorsUi: TUser[] = useMemo(
		() =>
			(Array.isArray(operatorsToNotify) ? operatorsToNotify : []).map(
				(op) =>
					({
						id: op.id,
						email: op.email,
						fullName: op.fullName,
					}) as TUser,
			),
		[operatorsToNotify],
	);

	const handleSave = () => {
		if (!emId) return;
		setSubmitted(true);
		const filters = [{ fieldName: 'emId', value: emId, operator: '=' }];
		const commonData = {
			emId: Number(emId),
			scheduledActionOn:
				scheduledActionOn && String(scheduledActionOn).trim() !== ''
					? String(scheduledActionOn)
					: null,
			operatorsToNotify: (Array.isArray(operatorsToNotify) ? operatorsToNotify : []).map(
				(operator: TEmCommentUser) => operator.id,
			),
			comment: String(comment ?? ''),
		};
		if (idToEdit && type === 'edit') {
			dispatch(
				updateEmCommentRequest({
					id: idToEdit ?? 0,
					data: commonData,
					getEmCommentsRequest: {
						pageNumber: 1,
						pageSize: PAGE_SIZE,
						orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
						filters,
					},
				}),
			);
		} else {
			dispatch(
				createEmCommentRequest({
					data: commonData,
					getEmCommentsRequest: {
						pageNumber: 1,
						pageSize: PAGE_SIZE,
						orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
						filters,
					},
				}),
			);
		}
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">{type === 'add' ? 'Add Comment' : 'Edit Comment'}</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: '8px 0px' }}>
					<TextField
						label="Comment"
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						multiline
						rows={4}
						fullWidth
					/>
					<DatePickerComponent
						id="scheduledActionOn"
						label="Scheduled Action On"
						value={scheduledActionOn ? DateTime.fromISO(String(scheduledActionOn)) : null}
						allowedRange={EDatePickerAllowedRange.FUTURE}
						onChange={(date) => {
							setScheduledActionOn(
								date
									? DateTime.utc(date.year, date.month, date.day).toISO({
											suppressMilliseconds: false,
										})
									: null,
							);
						}}
					/>
					{!!scheduledActionOn && (
						<Alert severity="warning" variant="filled">
							<Typography variant="body1">
								This comment will be added to the <b>Notifications</b> widget.
							</Typography>
						</Alert>
					)}
					<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
						{(operatorsUi ?? []).map((user) => (
							<UserAvatarMenu
								key={`${user.id}`}
								user={user}
								size="sm"
								onClickUser={(newUser) => {
									const current = Array.isArray(operatorsToNotify) ? [...operatorsToNotify] : [];
									if (newUser) {
										const index = current.findIndex((u) => u.id === user.id);
										if (index >= 0) {
											current[index] = {
												id: newUser.id,
												email: newUser.email,
												fullName: newUser.fullName,
											};
										}
									}
									setOperatorsToNotify(current);
								}}
								delateButton
								onDeleteUser={() => {
									const current = Array.isArray(operatorsToNotify) ? [...operatorsToNotify] : [];
									const index = current.findIndex((u) => u.id === user.id);
									if (index >= 0) current.splice(index, 1);
									setOperatorsToNotify(current);
								}}
							/>
						))}
						<Button
							size="small"
							color="primary"
							sx={{
								fontWeight: 550,
								fontSize: '0.9rem',
								padding: '2px 10px',
								textTransform: 'none',
								borderRadius: '12px',
								border: '1px dashed #016193',
							}}
							onClick={() => {
								const current = Array.isArray(operatorsToNotify) ? [...operatorsToNotify] : [];
								current.push({ id: 'new-user', fullName: 'New User', email: '' });
								setOperatorsToNotify(current);
							}}
						>
							Add User +
						</Button>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button onClick={handleSave} disabled={isSaving || String(comment).trim() === ''}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
